import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Layout from "../../Component/Layout";
import { PURCHASE_API } from "../../apiConfig";

// Same compact react-select style used on the Purchase Requisition page
const selectStyles = {
  control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: "13px", borderColor: "#dee2e6", boxShadow: "none" }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "32px", display: "flex", alignItems: "center" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
};

const styles = {
  pageTitleBar: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 },
  pageTitle: { fontSize: 26, fontWeight: 700, color: "#1e293b", marginBottom: 2 },
  pageSubtitle: { fontSize: 13, color: "#64748b" },
  filterCard: { background: "#ffffff", borderRadius: 8, padding: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 14 },
  filterLabel: { fontSize: 12, fontWeight: 600, marginBottom: 2, color: "#334155", display: "block" },
  primaryBtn: { paddingLeft: 14, paddingRight: 14 },
  tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
  erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
  erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
  statusPill: { fontSize: 12, fontWeight: 600, borderRadius: 999, padding: "3px 10px", display: "inline-block" },
  statusApproved: { background: "#eff6ff", color: "#2563eb" },
  statusPosted: { background: "#ecfdf5", color: "#059669" },
  selectedBadge: { fontSize: 13, color: "#0f172a", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, padding: "6px 12px", fontWeight: 600 },
  // ── View modal (read-only invoice detail) — same visual language & DOM
  //    structure as the Invoice Verification modal, so both screens feel
  //    like one consistent design. ──
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modal: { background: "#fff", borderRadius: 10, width: "100%", maxWidth: 1200, maxHeight: "92vh", overflowY: "auto", padding: "24px 28px", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" },
  modalHeaderTitle: { fontSize: 18, fontWeight: 700, marginBottom: 20, color: "#0f172a" },
  // Header fields laid out as a top-labelled grid — label above, value box
  // below — one field per row, matching Invoice Verification's layout.
  headerGrid: { display: "grid", gridTemplateColumns: "1fr", rowGap: 16, marginBottom: 20 },
  headerRow: { display: "flex", flexDirection: "column", gap: 6 },
  headerLabel: { fontSize: 13, fontWeight: 600, color: "#334155" },
  headerFieldRow: { display: "flex", gap: 8 },
  headerActual: { flex: 1, fontSize: 14, padding: "8px 10px", background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0", color: "#64748b" },
  badge: (label) => ({
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    color: label === "Payment Complete" ? "#166534" : label === "Approved" ? "#1e40af" : "#475569",
    background: label === "Payment Complete" ? "#dcfce7" : label === "Approved" ? "#dbeafe" : "#f1f5f9"
  }),
  gridWrap: { overflowX: "auto", marginTop: 10, border: "1px solid #e5e7eb", borderRadius: 8 },
  grid: { width: "100%", minWidth: 1400, borderCollapse: "collapse" },
  gridTh: { background: "#f8fafc", fontSize: 13, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left" },
  gridActualCell: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", background: "#fff", color: "#334155", whiteSpace: "nowrap", verticalAlign: "middle" },
  footerBar: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, paddingTop: 16, borderTop: "1px solid #e5e7eb" },
  closeBtn: { background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 6, padding: "8px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  // Tabs styled as the same dark/light pill segments as the Invoice
  // Verification modal's Product / Review tabs.
  tabBar: { display: "flex", gap: 8, margin: "16px 0" },
  tabBtn: (active) => ({
    padding: "6px 18px",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    background: active ? "#1e293b" : "#f1f5f9",
    color: active ? "#fff" : "#334155"
  }),
  // ── Make Payment modal ──
  paymentModal: { background: "#fff", borderRadius: 10, width: "100%", maxWidth: 460, padding: "24px 28px", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" },
  paymentSummary: { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px", marginBottom: 18, fontSize: 13, color: "#334155" },
  fieldGroup: { marginBottom: 16 },
  fieldLabel: { fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6, display: "block" },
  fieldInput: { width: "100%", fontSize: 14, padding: "8px 10px", borderRadius: 6, border: "1px solid #cbd5e1", outline: "none" },
  saveBtn: { background: "#1e293b", color: "#fff", border: "none", borderRadius: 6, padding: "8px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
};

// ── Everything shown in the "View" modal — full invoice header + every
//    product/cost/date column that InvoiceVerification captures. Read-only:
//    this screen never edits verification data, it only displays it. ──
const HEADER_FIELDS = [
  ["grn_number", "GRN Number"],
  ["purchase_order_no", "Purchase Order No"],
  ["supplier", "Supplier"],
  ["invoice_no", "Invoice No"]
];

// Shown only on the Basic tab, separate from the always-visible header
// fields above. Value comes straight from goods_receipt.payment_terms
// (see invoiceVerification.service.js) — nowhere else.
const BASIC_TAB_FIELDS = [
  ["payment_terms", "Payment Terms"]
];

const PRODUCT_COLUMNS = [
  ["product_code", "Product Code"],
  ["description", "Description"],
  ["currency", "Currency"],
  ["product_cost", "Product Cost"],
  ["batch_number", "Batch No"],
  ["uom", "UOM"],
  ["received_qty", "Received Qty"],
  ["total_cost", "Total Cost"],
  ["net_price", "Net Price"],
  ["cgst", "CGST"],
  ["sgst", "SGST"],
  ["igst", "IGST"],
  ["grand_total", "Grand Total"],
  ["delivery_date", "Delivery Date"],
  ["received_date", "Received Date"]
];

const displayValue = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  if (Array.isArray(val)) return val.filter(Boolean).join(", ") || "—";
  return String(val);
};

// Any field listed here is always rendered as dd/mm/yyyy, regardless of
// what format it's stored in (ISO string, Date object, etc.) — kept in
// sync with InvoiceVerification.js so dates read the same across screens.
const DATE_FIELDS = new Set(["delivery_date", "received_date", "postingDate"]);

const formatDate = (val) => {
  if (val === null || val === undefined || val === "") return null;
  const d = val instanceof Date ? val : new Date(val);
  if (isNaN(d.getTime())) return String(val); // unparsable — show raw rather than hide it
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// Same as displayValue, but formats known date fields as dd/mm/yyyy.
const displayCell = (key, val) => {
  if (DATE_FIELDS.has(key)) {
    if (val === null || val === undefined || val === "") return "—";
    return formatDate(val) || "—";
  }
  return displayValue(val);
};

// Fields shown in the View modal for an Account Posting row
const AP_VIEW_FIELDS = [
  ["companyCode", "Company Code"],
  ["location", "Location"],
  ["glCategory", "GL Category"],
  ["glType", "GL Type"],
  ["glCode", "GL Code"],
  ["description", "Description"],
  ["postingDate", "Posting Date"],
  ["amount", "Amount"],
  ["currency", "Currency"],
  ["supplierLabel", "Supplier"],
  ["reasonForPayment", "Reason for Payment"]
];

// Account Postings (with a supplier) are payable items too. Map them onto
// the same row shape the GRN rows use so the table/selection/payment UI
// works unchanged. Keys are prefixed "AP-" so they never clash with GRNs.
const toApRow = (r, tab) => ({
  source: "account_posting",
  ap_id: r.id,
  grn_number: `AP-${r.id}`,
  purchase_order_no: "", // intentionally blank for Account Posting rows
  invoice_no: r.glCode,
  vendor_name: r.supplierName,
  grand_total: r.amount,
  paid_amount: tab === "posted" ? r.totalPaid : r.paidAmount,
  outstanding_amount: r.outstandingAmount,
  currency: r.currency,
  payment_id: tab === "posted" ? `AP-${r.paymentId}` : undefined,
  bank_reference_number: r.bankReferenceNumber,
  payment_amount: r.paymentAmount,
  paid_at: r.paidAt,
  invoice_verification_status:
    tab === "posted"
      ? r.outstandingAmount <= 0.005 ? "Payment Complete" : "Partially Paid"
      : undefined
});

const PAGE_SIZE = 10;

// Only invoices in the "Approved for Payment" state are eligible
// for payment — that's the Active tab. Once paid, a GRN moves to
// "Payment Complete" and shows up in the Posted tab instead of
// disappearing. Selection (for processing payment) only applies to the
// Active tab and is restricted to a single supplier at a time so a
// batch payment always maps to one payee.
function SupplierPayment() {
  const [activeTab, setActiveTab] = useState("active"); // "active" | "posted"
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selected, setSelected] = useState({}); // { [grn_number]: true }
  const [page, setPage] = useState(1);

  // react-select filter values — mirrors the Purchase Requisition filter card
  const [filterGrn, setFilterGrn] = useState(null);
  const [filterPo, setFilterPo] = useState(null);
  const [filterInvoice, setFilterInvoice] = useState(null);
  const [filterVendor, setFilterVendor] = useState(null);

  // Badge counts shown on both tabs regardless of which one is active —
  // fetched independently of the main list so switching tabs doesn't
  // make the other tab's count disappear or go stale.
  const [counts, setCounts] = useState({ active: 0, posted: 0 });

  const loadCounts = () => {
    const emptyAp = () => ({ data: { data: [] } });
    Promise.all([
      axios.get(`${PURCHASE_API}/invoice-verification/approved-for-payment`),
      axios.get(`${PURCHASE_API}/invoice-verification/posted-payments`),
      axios.get(`${PURCHASE_API}/account-posting/payable`).catch(emptyAp),
      axios.get(`${PURCHASE_API}/account-posting/payments-posted`).catch(emptyAp)
    ])
      .then(([activeRes, postedRes, apActiveRes, apPostedRes]) => {
        setCounts({
          active: (activeRes.data?.data?.length || 0) + (apActiveRes.data?.data?.length || 0),
          posted: (postedRes.data?.data?.length || 0) + (apPostedRes.data?.data?.length || 0)
        });
      })
      .catch((err) => console.error("SUPPLIER PAYMENT COUNTS ERROR:", err));
  };

  // ── View modal state ──
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewDetail, setViewDetail] = useState(null); // { header, products }
  const [viewTab, setViewTab] = useState("basic"); // "basic" | "product" — visual tabs, same table underneath

  // ── Make Payment modal state ── opened by the "Make Payment" button
  // instead of processing immediately, so bank reference number + amount
  // can be captured before the selected GRNs move to Posted.
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [bankReference, setBankReference] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");

  const loadList = (tab = activeTab) => {
    setLoading(true);
    const endpoint =
      tab === "posted"
        ? `${PURCHASE_API}/invoice-verification/posted-payments`
        : `${PURCHASE_API}/invoice-verification/approved-for-payment`;
    const apEndpoint =
      tab === "posted"
        ? `${PURCHASE_API}/account-posting/payments-posted`
        : `${PURCHASE_API}/account-posting/payable`;
    Promise.all([
      axios.get(endpoint),
      axios.get(apEndpoint).catch((err) => {
        console.error("ACCOUNT POSTING PAYABLE ERROR:", err);
        return { data: { data: [] } };
      })
    ])
      .then(([res, apRes]) => {
        const apRows = (apRes.data?.data || []).map((r) => toApRow(r, tab));
        const data = [...(res.data?.data || []), ...apRows];
        setList(data);
        // Keep this tab's badge count in sync with what we just loaded.
        setCounts((prev) => ({ ...prev, [tab]: data.length }));
      })
      .catch((err) => {
        console.error("SUPPLIER PAYMENT LIST ERROR:", err);
        toast.error("Failed to load invoices");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setSelected({});
    setFilterGrn(null);
    setFilterPo(null);
    setFilterInvoice(null);
    setFilterVendor(null);
    setPage(1);
    loadList(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load both tabs' counts once up front so the inactive tab's badge is
  // correct even before the user ever switches to it.
  useEffect(() => {
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uniqueSorted = useCallback(
    (key) => [...new Set(list.map((r) => r[key]).filter(Boolean))].sort(),
    [list]
  );

  const grnOptions = useMemo(
    () =>
      [...new Set(list.filter((r) => r.source !== "account_posting").map((r) => r.grn_number).filter(Boolean))]
        .sort()
        .map((v) => ({ label: v, value: v })),
    [list]
  );
  const poOptions = useMemo(() => uniqueSorted("purchase_order_no").map((v) => ({ label: v, value: v })), [uniqueSorted]);
  const invoiceOptions = useMemo(() => uniqueSorted("invoice_no").map((v) => ({ label: v, value: v })), [uniqueSorted]);
  const vendorOptions = useMemo(() => uniqueSorted("vendor_name").map((v) => ({ label: v, value: v })), [uniqueSorted]);

  const filteredList = useMemo(
    () =>
      list.filter(
        (r) =>
          (!filterGrn || r.grn_number === filterGrn.value) &&
          (!filterPo || r.purchase_order_no === filterPo.value) &&
          (!filterInvoice || r.invoice_no === filterInvoice.value) &&
          (!filterVendor || r.vendor_name === filterVendor.value)
      ),
    [list, filterGrn, filterPo, filterInvoice, filterVendor]
  );

  useEffect(() => {
    setPage(1);
  }, [filterGrn, filterPo, filterInvoice, filterVendor]);

  const totalPages = Math.max(1, Math.ceil(filteredList.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedList = filteredList.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // The supplier currently "locked in" by whatever is already selected —
  // once you tick one invoice, only rows from that same supplier stay selectable.
  const selectedVendor = useMemo(() => {
    const selectedRow = list.find((r) => selected[r.grn_number]);
    return selectedRow?.vendor_name || null;
  }, [list, selected]);

  const selectedGrnNumbers = useMemo(
    () => Object.keys(selected).filter((grn) => selected[grn]),
    [selected]
  );

  // Sum of OUTSTANDING balance (not grand_total) for whatever's selected —
  // this is what the Make Payment modal defaults to and validates against,
  // so a GRN that's already been partially paid only asks for what's left.
  const selectedTotal = useMemo(
    () =>
      list
        .filter((r) => selected[r.grn_number])
        .reduce((sum, r) => sum + (Number(r.outstanding_amount) || 0), 0),
    [list, selected]
  );

  // Partial payment (amount less than the full outstanding balance) is only
  // allowed when exactly one invoice is selected — with multiple selected,
  // the modal forces the full combined outstanding amount.
  const partialAllowed = selectedGrnNumbers.length === 1;

  const toggleRow = (row) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[row.grn_number]) {
        delete next[row.grn_number];
      } else {
        next[row.grn_number] = true;
      }
      return next;
    });
  };

  // GRN invoices and Account Postings are paid through different
  // endpoints, so one payment batch can't mix the two.
  const selectedSource = useMemo(() => {
    const selectedRow = list.find((r) => selected[r.grn_number]);
    return selectedRow ? selectedRow.source || "grn" : null;
  }, [list, selected]);

  const isRowDisabled = useCallback(
    (row) =>
      (Boolean(selectedVendor) && row.vendor_name !== selectedVendor) ||
      (Boolean(selectedSource) && (row.source || "grn") !== selectedSource),
    [selectedVendor, selectedSource]
  );

  // Select-all only ever selects rows for a single supplier (same rule as
  // ticking rows one by one) — anchors on whichever supplier is already
  // selected, or the first row on the page if nothing is selected yet.
  const selectableRows = useMemo(
    () => paginatedList.filter((r) => !isRowDisabled(r)),
    [paginatedList, isRowDisabled]
  );

  const allSelectableChecked =
    selectableRows.length > 0 && selectableRows.every((r) => selected[r.grn_number]);

  const toggleSelectAll = (checked) => {
    if (!checked) {
      setSelected((prev) => {
        const next = { ...prev };
        selectableRows.forEach((r) => delete next[r.grn_number]);
        return next;
      });
      return;
    }
    const anchor = list.find((r) => selected[r.grn_number]) || paginatedList[0];
    if (!anchor || !anchor.vendor_name) return;
    const vendor = anchor.vendor_name;
    const source = anchor.source || "grn";
    setSelected((prev) => {
      const next = { ...prev };
      paginatedList.forEach((r) => {
        if (r.vendor_name === vendor && (r.source || "grn") === source) next[r.grn_number] = true;
      });
      return next;
    });
  };

  const clearSelection = () => setSelected({});

  // Opens the payment modal, pre-filling Amount with the selected
  // invoices' combined grand total (still editable) so the common case
  // needs no typing at all.
  const openPaymentModal = () => {
    if (selectedGrnNumbers.length === 0) return;
    setBankReference("");
    setPaymentAmount(selectedTotal ? selectedTotal.toFixed(2) : "");
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    if (processing) return;
    setShowPaymentModal(false);
    setBankReference("");
    setPaymentAmount("");
  };

  const handleProcessPayment = async () => {
    if (selectedGrnNumbers.length === 0) return;
    if (!bankReference.trim()) {
      toast.error("Bank reference number is required");
      return;
    }
    const amountNum = Number(paymentAmount);
    if (!paymentAmount || isNaN(amountNum) || amountNum <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    // Mirror the backend rule client-side for instant feedback: with
    // multiple invoices selected, the amount can't be less than the full
    // combined outstanding balance — only a single invoice can be paid partially.
    if (!partialAllowed && amountNum + 0.005 < selectedTotal) {
      toast.error(
        `Multiple invoices are selected — amount must be the full outstanding balance of ${selectedTotal.toFixed(2)}, or select just one invoice to pay partially.`
      );
      return;
    }
    if (partialAllowed && amountNum - 0.005 > selectedTotal) {
      toast.error(`Amount exceeds the outstanding balance of ${selectedTotal.toFixed(2)}`);
      return;
    }
    setProcessing(true);
    try {
      let res;
      if (selectedSource === "account_posting") {
        res = await axios.post(`${PURCHASE_API}/account-posting/pay`, {
          ids: list.filter((r) => selected[r.grn_number]).map((r) => r.ap_id),
          bank_reference_number: bankReference.trim(),
          amount: amountNum
        });
      } else {
        res = await axios.put(`${PURCHASE_API}/invoice-verification/process-payment`, {
          grn_numbers: selectedGrnNumbers,
          bank_reference_number: bankReference.trim(),
          amount: amountNum
        });
      }
      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to process payment");
        return;
      }
      if (res.data.is_partial) {
        const remaining = res.data.remaining_outstanding?.[0]?.outstanding_amount ?? 0;
        toast.success(
          `Partial payment of ${amountNum.toFixed(2)} recorded for ${selectedGrnNumbers[0]} — ${Number(remaining).toFixed(2)} remaining, still in Active`
        );
      } else {
        toast.success(
          `Payment completed for ${res.data.fully_paid_grns?.length || selectedGrnNumbers.length} invoice(s) — ${selectedVendor}`
        );
      }
      clearSelection();
      setShowPaymentModal(false);
      setBankReference("");
      setPaymentAmount("");
      // Only reload/switch to Posted if something actually moved there —
      // a partial payment leaves the GRN sitting in Active instead.
      if (res.data.is_partial) {
        loadList("active");
      } else {
        setActiveTab("posted");
      }
      loadCounts();
    } catch (err) {
      console.error("PROCESS PAYMENT ERROR:", err);
      toast.error(err?.response?.data?.message || "Failed to process payment");
    } finally {
      setProcessing(false);
    }
  };

  // ── View: pulls the same GRN detail the verification screen uses
  //    (header + product/cost lines) and shows it read-only. ──
  const openViewModal = async (grnNumber) => {
    setShowViewModal(true);
    setViewLoading(true);
    setViewDetail(null);
    setViewTab("basic");
    try {
      // Account Posting rows: show the posting record itself
      if (String(grnNumber).startsWith("AP-")) {
        const apRes = await axios.get(`${PURCHASE_API}/account-posting/${String(grnNumber).slice(3)}`);
        setViewDetail({ ap: apRes.data });
        return;
      }
      const res = await axios.get(`${PURCHASE_API}/invoice-verification/grn/${grnNumber}`);
      if (!res.data?.success) {
        toast.error("Could not load invoice details");
        setShowViewModal(false);
        return;
      }
      const { header, products } = res.data;
      setViewDetail({ header, products });
    } catch (err) {
      console.error("VIEW INVOICE DETAIL ERROR:", err);
      toast.error("Failed to load invoice details");
      setShowViewModal(false);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewDetail(null);
    setViewTab("basic");
  };

  return (
    <Layout>
      <style>{`
        .pr-tab-toggle { display: inline-flex; background: #f1f5f9; border-radius: 8px; padding: 3px; gap: 2px; }
        .pr-tab-toggle button { border: none; background: transparent; border-radius: 6px; padding: 5px 20px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.15s ease; display: flex; align-items: center; gap: 6px; }
        .pr-tab-toggle button.pr-active-tab { background: #fff; color: #1e293b; box-shadow: 0 1px 3px rgba(0,0,0,0.12); }
        .pr-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; border-radius: 9px; font-size: 11px; font-weight: 700; padding: 0 5px; line-height: 1; }
        .pr-badge-active { background: #dbeafe; color: #1d4ed8; }
        .pr-badge-posted { background: #dcfce7; color: #15803d; }
      `}</style>

      {/* ── Page Title ── */}
      <div style={styles.pageTitleBar}>
        <div>
          <h2 style={styles.pageTitle}>Supplier Payment</h2>
          <div style={styles.pageSubtitle}>
            {activeTab === "posted"
              ? "Every payment made so far, including partial payments."
              : "Select approved invoices from a single supplier and process payment."}
          </div>
        </div>
        {activeTab === "active" && (
          <div className="d-flex align-items-center gap-2">
         
            <button
              className="btn btn-dark"
              style={styles.primaryBtn}
              disabled={selectedGrnNumbers.length === 0 || processing}
              onClick={openPaymentModal}
            >
              {processing ? "Processing..." : ` Make Payment (${selectedGrnNumbers.length})`}
            </button>
          </div>
        )}
      </div>

      {/* ── Filters ── */}
      <div style={styles.filterCard}>
        <div className="row g-2">
          <div className="col-md-3 col-sm-6">
            <label style={styles.filterLabel}>GRN Number</label>
            <Select options={grnOptions} value={filterGrn} onChange={setFilterGrn} isClearable placeholder="Select" styles={selectStyles} />
          </div>
          <div className="col-md-3 col-sm-6">
            <label style={styles.filterLabel}>Purchase Order No.</label>
            <Select options={poOptions} value={filterPo} onChange={setFilterPo} isClearable placeholder="Select" styles={selectStyles} />
          </div>
          <div className="col-md-3 col-sm-6">
            <label style={styles.filterLabel}>Invoice No.</label>
            <Select options={invoiceOptions} value={filterInvoice} onChange={setFilterInvoice} isClearable placeholder="Select" styles={selectStyles} />
          </div>
          <div className="col-md-3 col-sm-6">
            <label style={styles.filterLabel}>Vendor Name</label>
            <Select options={vendorOptions} value={filterVendor} onChange={setFilterVendor} isClearable placeholder="Select" styles={selectStyles} />
          </div>
        </div>
      </div>

      {/* ── Active / Posted Toggle ── */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <div className="pr-tab-toggle">
          <button className={activeTab === "active" ? "pr-active-tab" : ""} onClick={() => setActiveTab("active")}>
            Active {counts.active > 0 && <span className="pr-badge pr-badge-active">{counts.active}</span>}
          </button>
          <button className={activeTab === "posted" ? "pr-active-tab" : ""} onClick={() => setActiveTab("posted")}>
            Posted {counts.posted > 0 && <span className="pr-badge pr-badge-posted">{counts.posted}</span>}
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={styles.tableContainer}>
        <div style={{ width: "100%", overflowX: "auto" }}>
          <table className="table" style={styles.erpTable}>
            <thead>
              <tr>
                {activeTab === "active" && (
                  <th style={{ ...styles.erpTh, width: 40, textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={allSelectableChecked}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      style={{ cursor: "pointer" }}
                    />
                  </th>
                )}
                <th style={styles.erpTh}>GRN Number</th>
                <th style={styles.erpTh}>Purchase Order No</th>
                <th style={styles.erpTh}>Invoice No</th>
                <th style={styles.erpTh}>Supplier</th>
                <th style={styles.erpTh}>Grand Total</th>
                {activeTab === "active" && <th style={styles.erpTh}>Paid So Far</th>}
                {activeTab === "active" && <th style={styles.erpTh}>Outstanding</th>}
                <th style={styles.erpTh}>Currency</th>
                {activeTab === "posted" && <th style={styles.erpTh}>Bank Reference No</th>}
                {activeTab === "posted" && <th style={styles.erpTh}>Amount Paid</th>}
                {activeTab === "posted" && <th style={styles.erpTh}>Paid At</th>}
                <th style={styles.erpTh}>Status</th>
                <th style={{ ...styles.erpTh, width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className="text-center p-4 text-muted">Loading…</td>
                </tr>
              ) : paginatedList.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center p-4 text-muted">
                    {activeTab === "posted" ? "No payments posted yet." : "No approved invoices awaiting payment."}
                  </td>
                </tr>
              ) : (
                paginatedList.map((row, index) => {
                  const disabled = activeTab === "active" && isRowDisabled(row);
                  // Posted tab now lists one row per PAYMENT (a GRN paid in
                  // instalments has several), so payment_id — not
                  // grn_number — is the unique key there.
                  const rowKey = activeTab === "posted" ? row.payment_id : row.grn_number;
                  const isPartiallyPaid =
                    activeTab === "active" && Number(row.paid_amount) > 0.005;
                  return (
                    <tr
                      key={rowKey}
                      style={{
                        background: selected[row.grn_number] ? "#eff6ff" : index % 2 === 0 ? "#fff" : "#fafbfc",
                        opacity: disabled ? 0.5 : 1
                      }}
                    >
                      {activeTab === "active" && (
                        <td style={{ ...styles.erpTd, textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={Boolean(selected[row.grn_number])}
                            disabled={disabled}
                            onChange={() => toggleRow(row)}
                            style={{ cursor: disabled ? "not-allowed" : "pointer" }}
                          />
                        </td>
                      )}
                      <td style={{ ...styles.erpTd, fontWeight: 600 }}>{row.source === "account_posting" ? "" : row.grn_number}</td>
                      <td style={styles.erpTd}>{row.source === "account_posting" ? "" : row.purchase_order_no || "—"}</td>
                      <td style={styles.erpTd}>{row.invoice_no || "—"}</td>
                      <td style={styles.erpTd}>{row.vendor_name || "—"}</td>
                      <td style={styles.erpTd}>
                        {row.grand_total !== null && row.grand_total !== undefined
                          ? Number(row.grand_total).toFixed(2)
                          : "—"}
                      </td>
                      {activeTab === "active" && (
                        <td style={styles.erpTd}>
                          {row.paid_amount !== null && row.paid_amount !== undefined
                            ? Number(row.paid_amount).toFixed(2)
                            : "0.00"}
                        </td>
                      )}
                      {activeTab === "active" && (
                        <td style={{ ...styles.erpTd, fontWeight: 600 }}>
                          {row.outstanding_amount !== null && row.outstanding_amount !== undefined
                            ? Number(row.outstanding_amount).toFixed(2)
                            : "—"}
                        </td>
                      )}
                      <td style={styles.erpTd}>{row.currency || "—"}</td>
                      {activeTab === "posted" && (
                        <td style={styles.erpTd}>{row.bank_reference_number || "—"}</td>
                      )}
                      {activeTab === "posted" && (
                        <td style={styles.erpTd}>
                          {row.payment_amount !== null && row.payment_amount !== undefined
                            ? Number(row.payment_amount).toFixed(2)
                            : "—"}
                        </td>
                      )}
                      {activeTab === "posted" && (
                        <td style={styles.erpTd}>
                          {row.paid_at ? formatDate(row.paid_at) : "—"}
                        </td>
                      )}
                      <td style={styles.erpTd}>
                        {activeTab === "posted" ? (
                          // This particular row is one payment — the badge reflects
                          // whether ITS GRN is now fully settled or still has a
                          // balance left (it may still be sitting in Active too).
                          row.invoice_verification_status === "Payment Complete" ? (
                            <span style={{ ...styles.statusPill, ...styles.statusPosted }}>Payment Complete</span>
                          ) : (
                            <span style={{ ...styles.statusPill, background: "#fef3c7", color: "#b45309" }}>
                              Partially Paid
                            </span>
                          )
                        ) : isPartiallyPaid ? (
                          <span style={{ ...styles.statusPill, background: "#fef3c7", color: "#b45309" }}>
                            Partially Paid
                          </span>
                        ) : (
                          <span style={{ ...styles.statusPill, ...styles.statusApproved }}>Approved for Payment</span>
                        )}
                      </td>
                      <td style={styles.erpTd}>
                        <button
                          className="btn btn-outline-dark btn-sm"
                          style={{ fontSize: "11px", padding: "2px 8px" }}
                          onClick={() => openViewModal(row.grn_number)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex align-items-center justify-content-between pt-3">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Page <b>{safePage}</b> of <b>{totalPages}</b> &bull; Total {filteredList.length}
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm" disabled={safePage <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
            <button className="btn btn-outline-secondary btn-sm" disabled={safePage >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      </div>

      {/* ── View modal: read-only invoice detail — same overlay/modal/header/
           footer structure as the Invoice Verification modal ── */}
      {showViewModal && (
        <div style={styles.overlay} onClick={closeViewModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            {viewLoading || !viewDetail ? (
              <div className="text-center p-5 text-muted">Loading invoice…</div>
            ) : viewDetail.ap ? (
              <>
                <div style={styles.modalHeaderTitle}>
                  Account Posting — {viewDetail.ap.glCode}
                </div>
                <div style={styles.headerGrid}>
                  {AP_VIEW_FIELDS.map(([key, label]) => (
                    <div style={styles.headerRow} key={key}>
                      <div style={styles.headerLabel}>{label}</div>
                      <div style={styles.headerFieldRow}>
                        <div style={styles.headerActual}>{displayCell(key, viewDetail.ap[key])}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={styles.footerBar}>
                  <button style={styles.closeBtn} onClick={closeViewModal}>Close</button>
                </div>
              </>
            ) : (
              <>
                <div style={styles.modalHeaderTitle}>
                  Invoice — GRN: {viewDetail.header.grn_number}
                </div>

                {/* Header fields — top-labelled grid, one field per row,
                    same layout as the Invoice Verification modal */}
                <div style={styles.headerGrid}>
                  {HEADER_FIELDS.map(([key, label]) => (
                    <div style={styles.headerRow} key={key}>
                      <div style={styles.headerLabel}>{label}</div>
                      <div style={styles.headerFieldRow}>
                        <div style={styles.headerActual}>{displayValue(viewDetail.header[key])}</div>
                      </div>
                    </div>
                  ))}
                </div>

                

                <div style={styles.tabBar}>
                  <button style={styles.tabBtn(viewTab === "basic")} onClick={() => setViewTab("basic")}>Basic</button>
                  <button style={styles.tabBtn(viewTab === "product")} onClick={() => setViewTab("product")}>Product</button>
                </div>

                {viewTab === "basic" && (
                  <div style={styles.headerGrid}>
                    {BASIC_TAB_FIELDS.map(([key, label]) => (
                      <div style={styles.headerRow} key={key}>
                        <div style={styles.headerLabel}>{label}</div>
                        <div style={styles.headerFieldRow}>
                          <div style={styles.headerActual}>{displayValue(viewDetail.header[key])}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {viewTab === "product" && (
                  <div style={styles.gridWrap}>
                    <table style={styles.grid}>
                      <thead>
                        <tr>
                          {PRODUCT_COLUMNS.map(([key, label]) => (
                            <th style={styles.gridTh} key={key}>{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {viewDetail.products.map((row, idx) => (
                          <tr key={idx}>
                            {PRODUCT_COLUMNS.map(([key]) => (
                              <td style={styles.gridActualCell} key={key}>{displayCell(key, row[key])}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div style={styles.footerBar}>
                  <button style={styles.closeBtn} onClick={closeViewModal}>Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Make Payment modal: Bank Reference Number + Amount for the
           selected GRNs. Save posts the payment and — on success — the
           list switches to the Posted tab where these GRNs now live. ── */}
      {showPaymentModal && (
        <div style={styles.overlay} onClick={closePaymentModal}>
          <div style={styles.paymentModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeaderTitle}>Make Payment</div>

            <div style={styles.paymentSummary}>
              <div><b>{selectedGrnNumbers.length}</b> invoice(s) selected — <b>{selectedVendor}</b></div>
              <div style={{ marginTop: 4, color: "#64748b" }}>{selectedGrnNumbers.join(", ")}</div>
              <div style={{ marginTop: 6 }}>
                Outstanding balance: <b>{selectedTotal.toFixed(2)}</b>
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Bank Reference Number</label>
              <input
                type="text"
                style={styles.fieldInput}
                value={bankReference}
                onChange={(e) => setBankReference(e.target.value)}
                placeholder="Enter bank reference / UTR number"
                autoFocus
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Amount</label>
              <input
                type="number"
                style={{ ...styles.fieldInput, background: !partialAllowed ? "#f1f5f9" : "#fff" }}
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
                min="0"
                max={selectedTotal || undefined}
                step="0.01"
                // Multiple invoices selected → full payment only, amount is locked.
                readOnly={!partialAllowed}
              />
              {partialAllowed ? (
                <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                  You can pay less than the outstanding balance — the remaining amount
                  stays in Active for a later payment.
                </div>
              ) : (
                <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                  Multiple invoices selected — full outstanding balance is required.
                  Select just one invoice to pay it partially.
                </div>
              )}
            </div>

            <div style={styles.footerBar}>
              <button style={styles.closeBtn} onClick={closePaymentModal} disabled={processing}>Cancel</button>
              <button style={styles.saveBtn} onClick={handleProcessPayment} disabled={processing}>
                {processing ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </Layout>
  );
}

export default SupplierPayment;